import React from 'react';
import { ArrowDown, ArrowUp, Plus, Sparkles, Trash2 } from 'lucide-react';
import { TaskySelect } from '../tasky';
import {
  LpStage,
  LpWorkflowStages,
  LpBrief,
  LpOutlineSection,
  LpCopySection,
  LpStrategyOption,
  LpGenerationStage,
  LpWorkflowProject,
  LpReview,
} from '../../services/api/lpWorkflowService';
import LpWorkflowDesign from './LpWorkflowDesign';
import { LP_OBJECTIVES } from './LpWorkflowCreate';

export const SECTION_LABELS: Record<string, string> = {
  hero: 'ファーストビュー',
  problem: '課題・共感',
  solution: '解決策',
  features: '特徴',
  benefits: 'メリット',
  comparison: '比較',
  proof: '根拠・実例',
  testimonials: 'お客様の声',
  pricing: '価格・条件',
  steps: '使い方・流れ',
  faq: 'よくある質問',
  cta: '行動への案内',
};
const Field = ({
  label,
  value,
  onChange,
  rows = 2,
  maxLength = 3000,
}: {
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  rows?: number;
  maxLength?: number;
}) => (
  <label className="lpw-field">
    {label}
    <textarea
      rows={rows}
      maxLength={maxLength}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  </label>
);
interface Props {
  stage: LpStage;
  draft: LpWorkflowStages;
  project: LpWorkflowProject;
  onChange: <K extends LpStage>(
    stage: K,
    value: NonNullable<LpWorkflowStages[K]>,
  ) => void;
  onGenerate: (
    stage: LpGenerationStage,
    sectionId?: string,
    designId?: string,
  ) => void;
  onBuild: (designId: string) => void;
  onAttach: (sectionId: string, key: string | null) => void;
  onPreview: (candidate?: string) => void;
  candidatePreviews: Record<string, string>;
  busy: boolean;
}

export default function LpWorkflowFields({
  stage,
  draft,
  project,
  onChange,
  onGenerate,
  onBuild,
  onAttach,
  onPreview,
  candidatePreviews,
  busy,
}: Props) {
  if (stage === 'brief') {
    const value = draft.brief;
    const patch = (field: keyof LpBrief, text: string) =>
      onChange('brief', { ...value, [field]: text });
    return (
      <div className="lpw-form">
        <Field
          label="商品の事実・特徴"
          value={value.product_summary}
          onChange={(v) => patch('product_summary', v)}
          rows={5}
          maxLength={10000}
        />
        <div className="lpw-grid-2">
          <Field
            label="誰に伝えるか・どんな状況か"
            value={value.target}
            onChange={(v) => patch('target', v)}
            maxLength={2000}
          />
          <Field
            label="得られる便益"
            value={value.benefits}
            onChange={(v) => patch('benefits', v)}
          />
        </div>
        <div className="lpw-grid-2">
          <Field
            label="裏付けとなる事実・出典"
            value={value.evidence}
            onChange={(v) => patch('evidence', v)}
          />
          <Field
            label="価格・申込条件"
            value={value.offer}
            onChange={(v) => patch('offer', v)}
          />
        </div>
        <div className="lpw-grid-2">
          <Field
            label="購入・申込を迷う理由"
            value={value.objections}
            onChange={(v) => patch('objections', v)}
          />
          <Field
            label="流入広告で伝えていること"
            value={value.ad_message}
            onChange={(v) => patch('ad_message', v)}
          />
        </div>
        <Field
          label="ブランド・見た目の方針"
          value={value.brand_direction}
          onChange={(v) => patch('brand_direction', v)}
        />
        <div className="lpw-grid-2">
          <label className="lpw-field">
            LPの目的
            <TaskySelect
              className="font-normal"
              value={value.objective}
              onChange={(e) => patch('objective', e.target.value)}
            >
              {Object.entries(LP_OBJECTIVES).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </TaskySelect>
          </label>
          <Field
            label="ボタンの文言"
            value={value.cta_label}
            onChange={(v) => patch('cta_label', v)}
            rows={1}
            maxLength={80}
          />
        </div>
        <label className="lpw-field">
          ボタンの遷移先URL
          <input
            type="url"
            maxLength={2000}
            value={value.cta_url || ''}
            onChange={(e) => patch('cta_url', e.target.value)}
            placeholder="https://..."
          />
        </label>
        <Field
          label="不足している情報・確認事項"
          value={value.missing_information}
          onChange={(v) => patch('missing_information', v)}
        />
      </div>
    );
  }
  if (stage === 'strategy') {
    const value = draft.strategy;
    if (!value)
      return (
        <Empty text="商品の強みと顧客の状況をつなぐ訴求を、理由付きで提案します。" />
      );
    const patch = (id: string, field: keyof LpStrategyOption, text: string) =>
      onChange('strategy', {
        ...value,
        options: value.options.map((o) =>
          o.id === id ? { ...o, [field]: text } : o,
        ),
      });
    return (
      <div className="lpw-option-grid">
        {value.options.map((option, index) => (
          <article
            className={`lpw-option ${value.selected_id === option.id ? 'selected' : ''}`}
            key={option.id}
          >
            <label className="lpw-option-heading">
              <input
                type="radio"
                name="strategy"
                checked={value.selected_id === option.id}
                onChange={() =>
                  onChange('strategy', { ...value, selected_id: option.id })
                }
              />
              <span>訴求案 {index + 1}</span>
              {value.selected_id === option.id && <b>採用する案</b>}
            </label>
            <Field
              label="訴求名"
              value={option.name}
              onChange={(v) => patch(option.id, 'name', v)}
              maxLength={120}
            />
            <Field
              label="約束する価値"
              value={option.promise}
              onChange={(v) => patch(option.id, 'promise', v)}
              maxLength={1000}
            />
            <Field
              label="対象顧客・状況"
              value={option.audience}
              onChange={(v) => patch(option.id, 'audience', v)}
              maxLength={1000}
            />
            <Field
              label="この商品・顧客に合う理由"
              value={option.reason}
              onChange={(v) => patch(option.id, 'reason', v)}
              rows={4}
              maxLength={2000}
            />
            <Field
              label="根拠・不足する材料"
              value={option.evidence}
              onChange={(v) => patch(option.id, 'evidence', v)}
            />
            <Field
              label="懸念の解消方法"
              value={option.objection}
              onChange={(v) => patch(option.id, 'objection', v)}
            />
          </article>
        ))}
      </div>
    );
  }
  if (stage === 'outline') {
    const value = draft.outline;
    if (!value)
      return (
        <Empty text="採用した訴求から、読み手が納得できる順序を組み立てます。" />
      );
    const patch = (id: string, field: keyof LpOutlineSection, text: string) =>
      onChange('outline', {
        sections: value.sections.map((s) =>
          s.id === id ? { ...s, [field]: text } : s,
        ),
      });
    const move = (index: number, delta: number) => {
      const sections = [...value.sections];
      [sections[index], sections[index + delta]] = [
        sections[index + delta],
        sections[index],
      ];
      onChange('outline', { sections });
    };
    return (
      <div className="lpw-form">
        {value.sections.map((section, index) => (
          <article className="lpw-section-card" key={section.id}>
            <div className="lpw-section-heading">
              <span className="lpw-number">{index + 1}</span>
              <strong>{SECTION_LABELS[section.type] || section.type}</strong>
              <div className="lpw-section-actions">
                <button
                  type="button"
                  aria-label={`${index + 1}番目を上へ`}
                  disabled={index <= 1}
                  onClick={() => move(index, -1)}
                >
                  <ArrowUp size={16} />
                </button>
                <button
                  type="button"
                  aria-label={`${index + 1}番目を下へ`}
                  disabled={index === 0 || index === value.sections.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <ArrowDown size={16} />
                </button>
                <button
                  type="button"
                  aria-label={`${index + 1}番目を削除`}
                  disabled={index === 0 || value.sections.length <= 2}
                  onClick={() =>
                    onChange('outline', {
                      sections: value.sections.filter(
                        (s) => s.id !== section.id,
                      ),
                    })
                  }
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            {index > 0 && (
              <label className="lpw-field">
                セクションの種類
                <TaskySelect
                  className="font-normal"
                  value={section.type}
                  onChange={(e) => patch(section.id, 'type', e.target.value)}
                >
                  {Object.entries(SECTION_LABELS)
                    .filter(([key]) => key !== 'hero')
                    .map(([key, name]) => (
                      <option key={key} value={key}>
                        {name}
                      </option>
                    ))}
                </TaskySelect>
              </label>
            )}
            <Field
              label="伝えるテーマ"
              value={section.title}
              onChange={(v) => patch(section.id, 'title', v)}
              maxLength={300}
            />
            <div className="lpw-grid-2">
              <Field
                label="このセクションの役割"
                value={section.purpose}
                onChange={(v) => patch(section.id, 'purpose', v)}
                maxLength={1000}
              />
              <Field
                label="伝える要点"
                value={section.key_points}
                onChange={(v) => patch(section.id, 'key_points', v)}
                maxLength={2000}
              />
            </div>
            <Field
              label="使う根拠・素材"
              value={section.evidence}
              onChange={(v) => patch(section.id, 'evidence', v)}
            />
          </article>
        ))}
        <button
          className="lpw-button secondary"
          disabled={value.sections.length >= 12}
          type="button"
          onClick={() =>
            onChange('outline', {
              sections: [
                ...value.sections,
                {
                  id: `section-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                  type: 'features',
                  title: '',
                  purpose: '',
                  key_points: '',
                  evidence: '',
                },
              ],
            })
          }
        >
          <Plus size={16} />
          セクションを追加
        </button>
      </div>
    );
  }
  if (stage === 'copy') {
    const value = draft.copy;
    if (!value)
      return (
        <Empty text="確定した構成に沿って、見出し・本文・CTA・注釈を作成します。" />
      );
    const patch = (id: string, field: keyof LpCopySection, text: string) =>
      onChange('copy', {
        ...value,
        sections: value.sections.map((s) =>
          s.id === id ? { ...s, [field]: text } : s,
        ),
      });
    return (
      <div className="lpw-form">
        {value.headlines.length > 0 && (
          <div className="lpw-headlines">
            <strong>ファーストビューの見出し案</strong>
            {value.headlines.map((title, i) => (
              <button
                className={value.sections[0]?.title === title ? 'selected' : ''}
                type="button"
                key={i}
                onClick={() => {
                  if (value.sections[0])
                    patch(value.sections[0].id, 'title', title);
                }}
              >
                {title}
              </button>
            ))}
          </div>
        )}
        {value.sections.map((section, index) => (
          <article key={section.id} className="lpw-section-card">
            <div className="lpw-section-heading">
              <span className="lpw-number">{index + 1}</span>
              <strong>
                {
                  draft.outline?.sections.find((s) => s.id === section.id)
                    ?.title
                }
              </strong>
              <button
                className="lpw-button small secondary"
                type="button"
                disabled={busy}
                onClick={() => onGenerate('copy', section.id)}
              >
                <Sparkles size={14} />
                この文章をAIで修正
              </button>
            </div>
            <Field
              label="見出し"
              value={section.title}
              onChange={(v) => patch(section.id, 'title', v)}
              maxLength={300}
            />
            <Field
              label="本文"
              value={section.content}
              onChange={(v) => patch(section.id, 'content', v)}
              rows={6}
            />
            <div className="lpw-grid-2">
              <Field
                label="ボタンの文言（配置しない場合は空欄）"
                value={section.cta_label}
                onChange={(v) => patch(section.id, 'cta_label', v)}
                rows={1}
                maxLength={80}
              />
              <Field
                label="公開する注釈・条件"
                value={section.notes}
                onChange={(v) => patch(section.id, 'notes', v)}
                maxLength={1000}
              />
            </div>
            <Field
              label="使った根拠（編集者向け・公開されません）"
              value={section.evidence}
              onChange={(v) => patch(section.id, 'evidence', v)}
            />
          </article>
        ))}
      </div>
    );
  }
  if (stage === 'design') {
    if (!draft.design)
      return (
        <Empty text="商品の実物素材とコピーをもとに、全体デザインの方向性を3案提案します。" />
      );
    return (
      <LpWorkflowDesign
        value={draft.design}
        project={project}
        busy={busy}
        onChange={(value) => onChange('design', value)}
        onGenerate={onGenerate}
        onBuild={onBuild}
        onAttach={onAttach}
        onPreview={onPreview}
        previews={candidatePreviews}
      />
    );
  }
  const review: LpReview = draft.review ?? { issues: [], checks: {} };
  return (
    <div className="lpw-form">
      {!draft.review && (
        <Empty text="AIが商品の元情報とコピーを照合します。表示とリンクの動作はプレビューで確認してください。" />
      )}
      {draft.review && (
        <div className={review.issues.length ? 'lpw-note' : 'lpw-success'}>
          {review.issues.length
            ? `${review.issues.length}件の確認事項があります。`
            : 'AIの照合で指摘はありませんでした。表示とリンクを確認して仕上げます。'}
        </div>
      )}
      {review.issues.map((issue, index) => (
        <div
          key={index}
          className={issue.severity === 'error' ? 'lpw-error' : 'lpw-note'}
        >
          <strong>
            {issue.severity === 'error' ? '修正が必要' : '確認してください'}
          </strong>
          <p>{issue.message}</p>
        </div>
      ))}
      {project.review_blockers
        .filter(
          (issue) => !review.issues.some((r) => r.message === issue.message),
        )
        .map((issue, index) => (
          <div key={index} className="lpw-error">
            {issue.message}
          </div>
        ))}
      <div className="lpw-review-checks">
        {(
          [
            ['facts', '実績・口コミ・価格・条件を元資料と照合しました'],
            ['desktop', 'PC表示で文字・画像・余白を確認しました'],
            ['mobile', 'スマホ表示で読みやすさと横はみ出しを確認しました'],
            [
              'cta',
              'CTAの遷移先を開き、申込・問い合わせ先の動作を確認しました',
            ],
          ] as const
        ).map(([key, label]) => (
          <label key={key}>
            <input
              type="checkbox"
              checked={!!review.checks?.[key]}
              onChange={(e) =>
                onChange('review', {
                  ...review,
                  checks: { ...review.checks, [key]: e.target.checked },
                })
              }
            />
            {label}
          </label>
        ))}
      </div>
      {project.workflow.brief.cta_url && (
        <a
          className="lpw-button secondary"
          href={project.workflow.brief.cta_url}
          target="_blank"
          rel="noreferrer"
        >
          実際のCTA遷移先を開く ↗
        </a>
      )}
    </div>
  );
}
function Empty({ text }: { text: string }) {
  return (
    <div className="lpw-empty">
      <Sparkles size={28} />
      <p>{text}</p>
    </div>
  );
}
